import React, { Suspense, useEffect, useState } from "react";
import Helmet from "react-helmet";
import { useCountryRedirectContext } from "./CountryRedirectContext";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { useUiMessageContext } from "../UIMessages/context";
import { getFlag } from "../../utils/i18nUtils";
import { defaultLanguageLookup, Language } from "../../config/languages";
import { Settings } from "../../types/Settings";
import { useSettings } from "../../hooks/SettingsHooks";
import { I18nextProvider } from "react-i18next";
import i18next, { i18n } from "i18next";
import { Site } from "../../config/sites";

function CountryRedirectHandlerInner({ lang }: { lang: Language }) {
  const settings: Settings = useSettings();
  const { path, site, url } = useCountryRedirectContext();
  const { getDismissedBannerMessages } = useUiMessageContext();
  const { sites } = settings;
  const t = useBjelinTranslations();
  const bannerKey = "country-redirect-" + site;
  const isDismissed = getDismissedBannerMessages().includes(bannerKey);

  const { displayMessage } = useUiMessageContext();
  useEffect(() => {
    if (!isDismissed && site && lang && url) {
      const domain = new URL(url).hostname;
      displayMessage({
        text: t("country-redirect.message", {
          region: sites?.[site as Site]?.name
        }),
        banner: true,
        identifier: bannerKey,
        persist: true,
        buttons: [
          {
            text: t("country-redirect.action", { site: domain }),
            url: url,
            type: "primary"
          }
        ],
        icon: (
          <span className="flagIcon msg-icon">
            <img src={getFlag(site, lang)} alt="" />
          </span>
        )
      });
    }
  }, [
    bannerKey,
    displayMessage,
    getDismissedBannerMessages,
    isDismissed,
    lang,
    site,
    sites,
    t,
    url
  ]);

  return !isDismissed && site && lang && url ? (
    <>
      <Helmet>
        <meta name={"country-redirect-site"} content={site} />
        <meta name={"country-redirect-path"} content={path} />
        <meta name={"country-redirect-url"} content={url} />
      </Helmet>
    </>
  ) : null;
}

export function CountryRedirectHandler() {
  const { site } = useCountryRedirectContext();
  const lang = site ? defaultLanguageLookup[site] : undefined;
  const [langI18nInstance, setLangI18nInstance] = useState<i18n>(i18next);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (lang) {
      setLangI18nInstance(
        i18next.cloneInstance(
          {
            lng: lang,
            fallbackLng: "en"
          },
          () => {
            setIsLoaded(true);
          }
        )
      );
    }
  }, [lang]);

  return lang ? (
    <Suspense>
      {isLoaded && (
        <I18nextProvider i18n={langI18nInstance}>
          <CountryRedirectHandlerInner lang={lang} />
        </I18nextProvider>
      )}
    </Suspense>
  ) : null;
}
